import { Injectable, inject } from '@angular/core';
import { AuthService } from './auth.service';
import { ScreenLockService } from './screen-lock.service';
import { environment } from '../../environments/environment';

const DEFAULT_IDLE_MS = 30 * 60 * 1000;
const DEFAULT_CHECK_INTERVAL_MS = 30_000;

const ACTIVITY_EVENTS: Array<keyof DocumentEventMap> = [
  'click',
  'keydown',
  'mousedown',
  'scroll',
  'touchstart',
];

/**
 * Ends the staff session on idle timeout or when refresh token is no longer valid,
 * and redirects via {@link AuthService.logout}.
 */
@Injectable({ providedIn: 'root' })
export class SessionTimeoutService {
  private readonly auth = inject(AuthService);
  private readonly screenLock = inject(ScreenLockService);

  private readonly idleTimeoutMs =
    environment.sessionIdleTimeoutMs ?? DEFAULT_IDLE_MS;
  private readonly checkIntervalMs =
    environment.sessionCheckIntervalMs ?? DEFAULT_CHECK_INTERVAL_MS;

  private started = false;
  private lastActivityAt = Date.now();
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private refreshInFlight = false;
  private readonly onActivity = (): void => this.markActivity();
  private readonly onVisibility = (): void => {
    if (document.visibilityState === 'visible') {
      this.evaluateSession('visible');
    }
  };

  start(): void {
    if (this.started) {
      return;
    }
    this.started = true;
    this.markActivity();
    for (const eventName of ACTIVITY_EVENTS) {
      document.addEventListener(eventName, this.onActivity, { passive: true });
    }
    document.addEventListener('visibilitychange', this.onVisibility);
    this.intervalId = setInterval(
      () => this.evaluateSession('interval'),
      this.checkIntervalMs,
    );
    this.evaluateSession('start');
  }

  stop(): void {
    if (!this.started) {
      return;
    }
    this.started = false;
    if (this.intervalId != null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    for (const eventName of ACTIVITY_EVENTS) {
      document.removeEventListener(eventName, this.onActivity);
    }
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  private markActivity(): void {
    this.lastActivityAt = Date.now();
  }

  private evaluateSession(_reason: string): void {
    if (this.screenLock.isLocked()) {
      return;
    }

    if (!this.auth.getAccessToken() && !this.auth.getRefreshToken()) {
      return;
    }

    if (!this.auth.hasValidSession()) {
      this.auth.logout();
      return;
    }

    if (this.idleTimeoutMs > 0 && this.isIdleExpired()) {
      this.auth.logout();
      return;
    }

    if (this.auth.isRefreshTokenExpired()) {
      this.auth.logout();
      return;
    }

    if (!this.auth.isAccessTokenExpired()) {
      return;
    }

    if (!this.auth.getRefreshToken()) {
      this.auth.logout();
      return;
    }

    if (this.refreshInFlight) {
      return;
    }

    this.refreshInFlight = true;
    this.auth.refreshSession().subscribe({
      next: () => {
        this.refreshInFlight = false;
      },
      error: () => {
        this.refreshInFlight = false;
        this.auth.logout();
      },
    });
  }

  private isIdleExpired(): boolean {
    return Date.now() - this.lastActivityAt >= this.idleTimeoutMs;
  }
}
